<template>
  <v-container
    id="launch"
    class="fill-height justify-center"
    tag="section"
  >
    <v-progress-circular v-if="busy" indeterminate large />
    <v-card v-if="!busy && !error">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <div>
          {{ $t('launch.step1.text') }} <v-stepper v-model="step" vertical class="no-gutter no-border">
          <v-stepper-step :complete="step > 1" step="1" class="cursor" @click="gotoStep(1)">
            {{ $t('launch.step1.title') }}
          </v-stepper-step>
          <v-stepper-content step="1">
            <div v-if="busy">
              <v-row>
                <v-col><v-progress-circular indeterminate /> busy</v-col>
              </v-row>
            </div>
            <div v-if="!busy">
              <p>{{ $t('launch.step1.subtitle') }}</p>
              <v-row :style="{padding:'6px'}">
                <v-col
                  v-for="platform of availablePlatforms"
                  :key="platform"
                  cols="12"
                  sm="4"
                >
                  <v-btn :disabled="!supported(platform)" tile block :class="platform" @click="selectPlatform(platform)">
                    <v-icon class="mr-2">
                      {{ iconForPlatform(platform) }}
                    </v-icon>
                    {{ $t(('platforms.' + platform)) }}
                  </v-btn>
                </v-col>
              </v-row>
              <br>
              <p v-if="availablePlatforms.includes('web')">
                {{ $t('launch.stream-guidance') }}
              </p>
              <p v-if="!streamingSupportedOnBrowser()">
              {{ $t('launch.stream-not-supported') }}
            </p>
            </div>
          </v-stepper-content>
          <v-stepper-step :complete="step > 2" step="2" class="cursor" @click="gotoStep(2)">
            {{ $t('launch.step2.title') }}
          </v-stepper-step>
          <v-stepper-content step="2">
            <p v-if="selectedPlatform">
              {{ $t('launch.step2.text', [$t(('platforms.' + selectedPlatform))]) }} <a target="_blank" :href="launchLink">{{ $t('launch.step2.here') }}</a>.
              <br>{{ $t('launch.install-warning') }}
            </p>
            <v-btn color="secondary" @click="step=3">
              {{ $t('global.continue') }}
            </v-btn>
          </v-stepper-content>
          <v-stepper-step :complete="step > 3" step="3" class="cursor" @click="gotoStep(3)">
            {{ $t('launch.step3.title') }}
          </v-stepper-step>
          <v-stepper-content step="3">
            <p>{{ $t('launch.step3.text') }}</p>
            <shortcode-generator ref="generator" mini :action="action" :invite-code="inviteCode" :account="source" :user="user" :module="getModuleId()" no-button auto-generate /> <br>
            <p>
              <i18n path="launch.step3.text2" tag="label" for="launch.step3.here">
                <a href="#" @click="refreshCode()">{{ $t('launch.step3.here') }}</a>
              </i18n>
            </p>
            <p>
              <i18n path="launch.step3.text3" tag="label" for="launch.step3.help-center">
                <a :href="codesHelpLink" target="_blank">{{ $t('launch.step3.help-center') }}</a>
              </i18n>
            </p>
          </v-stepper-content>
        </v-stepper>
        </div>
      </v-card-text>
    </v-card>
    <v-card v-if="error">
      <v-card-title>{{ $t('global.error') }}</v-card-title>
      <v-card-text v-html="error" />
    </v-card>
  </v-container>
</template>

<script>
  import 'firebase/auth'
  import api from '@/helpers/api.js'
  import shortcodeGenerator from '@/views/components/ShortcodeGenerator.vue'

  import supportedPlatforms from '@/cfg/supported-platforms'
  import commonLinks from '@/cfg/common-links'
  import shortcodeTypes from '@/cfg/shortCode-types'
  // import router from '@/router'


  const ThirdPartyShops = {
    AAP: 'aap',
  }

  export default {
    name: 'Launch',

    components: {
      'shortcode-generator': shortcodeGenerator,
    },

    data: () => ({
      busy: true,
      module: undefined,
      defaultLaunchLink: 'https://www.bodyswaps.co/download-bodyswaps',
      selectedPlatform: undefined,
      step: 1,
      codeGeneratorActivated: false,
      error: undefined,
      user: undefined,
      aapRedirectUrl: process.env.VUE_APP_BODYSWAPS_AAP_REDIRECT_LINK,
      aapAuthenticateUrl: process.env.VUE_APP_BODYSWAPS_AAP_AUTH_LINK,
      notLoginToken: '00000000-0000-0000-0000-000000000000',
      redirect: '',
      streamParameters: {},
      enabledPlatforms: undefined,
    }),

    computed: {
      action () {
        if (this.isJoinClass) {
          return shortcodeTypes.JoinClass
        } else if (this.source) {
          return shortcodeTypes.ThirdPartyUser
        } else {
          return shortcodeTypes.UserLogin
        }
      },
      lti () {
        return this.$route.query.integration === 'lti'
      },
      source () {
        return this.$route.query.source || this.$route.params.provider
      },
      inviteCode () {
        return this.$route.query.inviteCode ?? ''
      },
      launchLink () {
        return (this.module && this.module.launchLinks && this.module.launchLinks[this.selectedPlatform])
          ? this.module.launchLinks[this.selectedPlatform]
          : commonLinks.Installation.OTS[this.selectedPlatform]
      },
      streamLink () {
        // grab the streamParameters object and convert to query string, removing any undefined values
        return `stream?${Object.entries(this.streamParameters).filter(([key, value]) => value !== undefined).map(([key, value]) => `${key}=${value}`).join('&')}`
      },
      moduleID () {
        return this.$route.query.module
      },
      codesHelpLink () {
        return commonLinks.HelpCenter.accessCodes
      },
      isJoinClass () {
        return this.classLearner
      },
      title () {
        if (this.isJoinClass) {
          return this.$t('launch.join-class')
        } else {
          // Special case for CHEST!
          if (this.$route.query.source === 'chest') {
            return 'Launch First 5 Minutes® Modules'
          }
          return this.module ? this.module.title : ''
        }
      },
      availablePlatforms () {
        // each module can have a list of platforms it supports, if not then use the default list
        let platforms = (this.module && this.module.platforms && this.module.platforms.length > 0) ? this.module.platforms : supportedPlatforms.All

        // then on an account level they can filter off enabled platforms - either logged in or via the third-party-user registration route
        const enabledPlatforms = this.enabledPlatforms ?? this.$store.getters.currentUserProfile?.account?.enabledPlatforms

        if (enabledPlatforms && enabledPlatforms.length > 0) {
          platforms = platforms.filter(x => enabledPlatforms.includes(x))
        }

        if ((this.classLearner && !this.classLearner.streamingEnabled) ||
          (this.$store.getters.currentUserProfile && !this.$store.getters.currentUserProfile.account.streamingEnabled)) {
          console.log('streaming not authorised for this class learner')
          return platforms.filter(x => x !== supportedPlatforms.Streaming)
        }
        return platforms
      },
    },

    watch: {
      step (newValue, oldValue) {
        if (newValue === 3 && !this.codeGeneratorActivated) {
          this.codeGeneratorActivated = true
          this.$refs.generator.generateShortcode()
        }
      },
    },

    async mounted () {
      // console.log('env  = ' + JSON.stringify(process.env))
      // console.log('cookie', cookies.get('baseUrl'), 'env', process.env.VUE_APP_BODYSWAPS_API_ENDPOINT)

      // Check if account is suspended
      const isSuspended = this.$store.getters.currentUserProfile?.account?.suspended
      if (isSuspended) {
        this.error = this.$t('dashboard.account-expired-message')
        this.busy = false
        return
      }

      // *** Validate the expected query parameters ***
      // source is the third party integration name or in the case of LTI the accountId
      // module is the module ID
      // user is the user ID
      // nftoken is the token for the third party shop (this is just for AAP shop integration)
      // integration is the integration type (lti or thirdPartyShop)
      // redirect [optional] is the redirect URL for post completing the module
      // lang [optional] is the language to use for the module
      const { source, module, user, nftoken, nfToken, integration, redirect, lang, inviteCode, debug } = this.$route.query
      const provider = this.$route.params.provider
      this.busy = true
      if (redirect) {
        this.redirect = redirect
      }


      if (lang) {
        this.$i18n.locale = lang
      }

      // Check to see if this is a demo request - this is a special case for A/B testing
      const DEMO_STREAMING_ID = 'demo-streaming'
      const DEMO_NON_STREAMING_ID = 'demo-non-streaming'
      const DEMO_MODULE_ID = 'demo'
      
      if (source === DEMO_STREAMING_ID || source === DEMO_NON_STREAMING_ID) {
        try {
          const demoAccountId = (source === DEMO_STREAMING_ID) ? process.env.VUE_APP_STREAMING_DEMO_ACCOUNT : process.env.VUE_APP_NON_STREAMING_DEMO_ACCOUNT
          const { email, name } = this.$route.query
          const payload = await this.registerThirdPartyUser(demoAccountId, DEMO_MODULE_ID, email, null, name)
          this.module = payload.module
          this.user = payload.user
          this.enabledPlatforms = payload.enabledPlatforms ?? undefined
          this.streamParameters = {
            integration: source, // we need an integration in here
            // module: DEMO_MODULE_ID,
            title: 'Demo',
            lang: lang,
            user: payload.user,
          }
        } catch (ex) {
          console.log('demo-streaming call failed')
          this.error = this.$t('sorry-your-request-cannot-be-processed', [ex.response.status])
        }
      } else {
        // Set up configuration based on the query parameters
        // Launch routes: LTI, SSO, Unsecured LMS, Bodyswaps

        // LTI: If integration is set then attempt to use the LMS/integration route - we only support LTI currently
        if (integration) {
          if (integration === 'lti') {
            try {
              const payload = await this.registerThirdPartyUser(source, module, user, integration)
              this.module = payload.module
              this.user = payload.user
              this.enabledPlatforms = payload.enabledPlatforms ?? undefined
              this.streamParameters = {
                integration: 'lti',
                module: module,
                title: 'LtiIntegration',
                redirect: redirect,
                lang: lang,
                user: payload.user,
              }
              // return this.$router.push(`stream?integration=lti&module=${module}&title=LtiIntegration&redirect=${redirect}${lang ? `&lang=${lang}` : ''}`)
            } catch (e) {
              this.error = this.$t('sorry-your-request-cannot-be-processed', [e.response.data])
            }
          }
        }

        // SSO: If no integration but a provider is supplied then try AAP Third Party Shop integration (SSO)
        if (!integration && provider) {
          const anynftoken = nftoken ?? nfToken
          // Third party shop integration
          try {
            const payload = await this.thirdPartyShopIntegration(provider, anynftoken)
            // console.log('SSO: return payload = ' + JSON.stringify(payload))
            this.user = payload.user
          } catch (e) {
            if (e.response.data.message.status === 401 || e.response.data.message.status === 404) window.location.href = this.aapRedirectUrl
            this.error = this.$t('sorry-your-request-cannot-be-processed')
          }
        }

        // Unsecured LMS: If there is a source but no provider or integratio then this request is via an unsecured third party LMS - i.e. CHEST
        if (source && !provider && !integration) {
          try {
            const payload = await this.registerThirdPartyUser(source, module, user)
            // console.log('Unsecured LMS: return payload = ' + JSON.stringify(payload))
            this.module = payload.module
            this.user = payload.user
            this.streamParameters = {
              module: payload.module.uid,
              title: payload.module.title,
              redirect: redirect,
              lang: lang,
              user: payload.user,
              integration: 'unsecured',
            }
          } catch (e) {
            this.error = this.$t('sorry-your-request-cannot-be-processed', [e.response.data])
          }
        }

        // Bodyswaps: If no source or provider or integration this is a normal Bodyswaps User!
        if (!source && !provider && !integration) {
          // User is coming via normal Bodyswaps Go routes
          if (inviteCode) {
            // User is Joining a Bodyswaps Class (No integration)
            this.classLearner = await api.getClassLearnerByInviteCode(inviteCode)
            if (!this.classLearner) {
              this.error = this.$t('launch.invitation-code-not-found')
            }
            this.enabledPlatforms = this.classLearner.enabledPlatforms
            this.streamParameters.classLearner = this.classLearner.id
            this.streamParameters.title = 'Bodyswaps'
            this.streamParameters.lang = this.classLearner.languageCode ?? lang
          } else {
            // Normal logged in user
            // check if module ID has been passed, in while case try to get the meta-data for it
            if (module) {
              this.module = await api.getContent(module)
            }
            this.streamParameters = {
              module: module,
              title: this.module?.title ?? '',
              redirect: redirect,
              lang: lang,
            }
            // if neither meta-data found or module ID passed, then error
            if (!module || !this.module) {
              this.error = this.$t('launch.error-fetching-this-module')
              this.busy = false
            }
          }
        }

        if (debug) {
          console.log('Streaming in debug mode')
          this.streamParameters.debug = true
        }
      }

      // If streaming is the only option for this account then go straight through to streaming page
      if (this.isStreamingOnlyOption()) {
        // redirect to the streaming page
        this.$router.push(this.streamLink)
      }
      this.busy = false
    },

    methods: {
      getModuleId () {
        return (this.module?.id) ? this.module.id : ''
      },
      isStreamingOnlyOption () {
        // console.log('availablePlatforms = ' + this.availablePlatforms)
        return this.availablePlatforms.length === 1 && this.availablePlatforms[0] === supportedPlatforms.Streaming
      },
      isFirefox () {
        return typeof InstallTrigger !== 'undefined'
      },
      async registerThirdPartyUser (source, module, user, integration, name) {
        return await api.registerThirdPartyUser(source, module, user, (integration === 'lti'), name)
      },
      supported (platform) {
        if (platform === supportedPlatforms.Streaming)
        {
          // check if Firefox
          return true // !this.isFirefox()
        }
        return true
      },
      streamingSupportedOnBrowser () {
        return this.supported(supportedPlatforms.Streaming)
      },
      async thirdPartyShopIntegration (provider, nfToken) {
        if (provider === ThirdPartyShops.AAP) {
          if (nfToken) {
            if (nfToken === this.notLoginToken) {
              window.location.href = this.aapAuthenticateUrl
            } else {
              return await api.thirdPartyShopIntegration(nfToken, provider)
            }
          } else {
            window.location.href = this.aapRedirectUrl
          }
        } else {
          this.error = 'Invalid provider ID'
        }
      },
      iconForPlatform (platform) {
        switch (platform) {
          case supportedPlatforms.Quest:
          case supportedPlatforms.Pico:
          case supportedPlatforms.Vive:
            return 'mdi-panorama-sphere-outline'
          case supportedPlatforms.Android:
          case supportedPlatforms.IOS:
            return 'mdi-cellphone'
          default:
            return 'mdi-laptop'
        }
      },
      gotoStep (step) {
        if (this.selectedPlatform !== undefined) {
          this.step = step
        }
        if (step === 1) {
          this.selectedPlatform = undefined
        }
      },
      selectPlatform (platform) {
        // const launchLink = (this.module && this.module.launchLinks && this.module.launchLinks[platform]) ? this.module.launchLinks[platform] : this.defaultLaunchLink
        if (platform !== supportedPlatforms.Streaming) {
          this.selectedPlatform = platform
          this.step = 2
        } else {
          console.log('stream to ' + this.streamLink)

          // this.$router.push({ name: this.streamLink, params: { customParam: 'value' } })
          // this.$router.push(this.streamLink)
          this.$router.push({ name: 'Stream', query: this.streamParameters })
        }
      },
      refreshCode () {
        this.$refs.generator.generateShortcode()
      },
    },
  }
</script>

<style scoped>

.popup-medium {
  width: 640px;
  max-width: 640px;
}

.v-stepper {
  border: 0;
}

.v-stepper__step {
  font-size: 20px !important;
}

.cursor {
  cursor: pointer;
}

.no-border {
  border: 0 !important;
  box-shadow: none;
  -webkit-box-shadow: none;
}

.quest,.pico,.vive {
  background-color: #0096c7 !important;
}
.pc,.mac,.web {
  background-color: #ef476f !important;

}
.ios,.android {
  background-color: #59bbbb !important;
}


</style>
